/**
 * Service-session state — tray, cocktail counts, ingredient checks, filter.
 *
 * Persisted to localStorage so a tablet reload doesn't lose the bartender's
 * mid-service progress. Single storage key, JSON-encoded.
 *
 * The `query` (search input) is NOT in this store — it's transient per
 * keystroke. The filter pill IS persisted.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { FilterKind } from "../types/drink";

export const MAX_LOADED_DRINKS = 3;
export const DEFAULT_COCKTAIL_COUNT = 1;

function getActiveUserId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const cached = window.localStorage.getItem("station.cached_user");
    if (cached) {
      const user = JSON.parse(cached);
      return user.id || user.display_label || null;
    }
  } catch {}
  return null;
}

function getStorageKey(): string {
  const userId = getActiveUserId();
  return userId 
    ? `sentinel_os_state_${userId}` 
    : "station.session_state.v2";
}

export interface SessionState {
  /** Slugs of currently loaded drinks, in order they were loaded. Max 3. */
  loadedSlugs: string[];
  /** Slug of the recipe currently shown in the detail view. */
  activeSlug: string | null;
  /** Per-loaded-drink cocktail count (1..99). */
  cocktailCounts: Record<string, number>;
  /** Per-loaded-drink check state, keyed by recipe_ingredient.id. */
  checkedIngredients: Record<string, Record<string, boolean>>;
  /** Per-loaded-drink check state, keyed by recipe_finish_step.id. */
  checkedFinishSteps: Record<string, Record<string, boolean>>;
  /** Current filter pill. */
  filter: FilterKind;
}

const EMPTY_STATE: SessionState = {
  loadedSlugs: [],
  activeSlug: null,
  cocktailCounts: {},
  checkedIngredients: {},
  checkedFinishSteps: {},
  filter: "today",
};

function loadFromStorage(): SessionState {
  if (typeof window === "undefined") return EMPTY_STATE;
  try {
    const key = getStorageKey();
    let raw = window.localStorage.getItem(key);
    // Forward-migrate v1 state if present and v2 is empty (one-time, then v1 is dropped).
    if (!raw && key === "station.session_state.v2") {
      const legacy = window.localStorage.getItem("station.session_state.v1");
      if (legacy) {
        raw = legacy;
        try {
          window.localStorage.removeItem("station.session_state.v1");
        } catch {
          /* non-fatal */
        }
      }
    }
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return EMPTY_STATE;
    // Defensive shape check — drop unknown values, keep what's valid.
    // Per-slug values must validate or be dropped (corrupt storage with a
    // string count → NaN math through scaleLine until "End round").
    const cocktailCounts: Record<string, number> = {};
    if (parsed.cocktailCounts && typeof parsed.cocktailCounts === "object") {
      for (const [slug, n] of Object.entries(parsed.cocktailCounts)) {
        if (typeof n === "number" && Number.isFinite(n) && n >= 1 && n <= 99) {
          cocktailCounts[slug] = Math.floor(n);
        }
      }
    }
    function sanitizeChecks(obj: any): Record<string, Record<string, boolean>> {
      const out: Record<string, Record<string, boolean>> = {};
      if (!obj || typeof obj !== "object") return out;
      for (const [slug, marks] of Object.entries(obj)) {
        if (!marks || typeof marks !== "object") continue;
        const inner: Record<string, boolean> = {};
        for (const [id, v] of Object.entries(marks as object)) {
          if (typeof v === "boolean") inner[id] = v;
        }
        out[slug] = inner;
      }
      return out;
    }
    return {
      loadedSlugs: Array.isArray(parsed.loadedSlugs)
        ? parsed.loadedSlugs.filter((s: unknown) => typeof s === "string").slice(0, MAX_LOADED_DRINKS)
        : [],
      activeSlug: typeof parsed.activeSlug === "string" ? parsed.activeSlug : null,
      cocktailCounts,
      checkedIngredients: sanitizeChecks(parsed.checkedIngredients),
      checkedFinishSteps: sanitizeChecks(parsed.checkedFinishSteps),
      // Forward-migrate legacy values from earlier MVP (updated, 86d) → "today".
      filter: ["today", "popular", "all", "cocktails", "old_fashioned"].includes(parsed.filter) ? parsed.filter : "today",
    };
  } catch {
    return EMPTY_STATE;
  }
}

function saveToStorage(state: SessionState): void {
  if (typeof window === "undefined") return;
  try {
    const key = getStorageKey();
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Quota exceeded or unavailable — non-fatal
  }
}

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Drop the per-drink count + checks for a slug that's no longer loaded.
 * Keeps storage from growing unbounded.
 */
function gc(state: SessionState): SessionState {
  const keep = new Set(state.loadedSlugs);
  const cocktailCounts: Record<string, number> = {};
  const checkedIngredients: Record<string, Record<string, boolean>> = {};
  const checkedFinishSteps: Record<string, Record<string, boolean>> = {};
  for (const slug of keep) {
    if (state.cocktailCounts[slug] != null) cocktailCounts[slug] = state.cocktailCounts[slug];
    if (state.checkedIngredients[slug]) checkedIngredients[slug] = state.checkedIngredients[slug];
    if (state.checkedFinishSteps[slug]) checkedFinishSteps[slug] = state.checkedFinishSteps[slug];
  }
  return { ...state, cocktailCounts, checkedIngredients, checkedFinishSteps };
}

export interface SessionActions {
  /** From the recipe list — adds to tray, resets count + checks for this drink, sets active. */
  selectFromList: (slug: string) => void;
  /** From the tray — just sets active (slug must already be loaded). */
  selectFromTray: (slug: string) => void;
  /** Remove a drink from the tray; clears its count + checks. */
  removeLoaded: (slug: string) => void;
  /** Set count for a slug, clamped to [1,99]. */
  setCocktailCount: (slug: string, n: number) => void;
  /** Flip the checked state for a single ingredient on a single drink. */
  toggleIngredient: (slug: string, ingredientId: string) => void;
  /** Flip the checked state for a single finish step on a single drink. */
  toggleFinishStep: (slug: string, finishStepId: string) => void;
  /** Reset count to 1 + clear all ingredient + finish-step checks for one drink. */
  resetCocktail: (slug: string) => void;
  /** Clear ingredient AND finish-step checks; count preserved. */
  clearChecks: (slug: string) => void;
  /** End round: clear tray, all counts/checks, set filter back to "today". */
  endRound: () => void;
  /** Change filter pill. */
  setFilter: (filter: FilterKind) => void;
}

export function useSessionStore(): { state: SessionState } & SessionActions {
  const [state, setState] = useState<SessionState>(loadFromStorage);

  // Trigger state reload if the storage key changes (e.g. user swaps or signs out)
  const currentKey = typeof window !== "undefined" ? getStorageKey() : "";
  useEffect(() => {
    setState(loadFromStorage());
  }, [currentKey]);

  useEffect(() => {
    saveToStorage(state);
  }, [state]);

  const selectFromList = useCallback((slug: string) => {
    setState((s) => {
      // Add to loaded if not present (max 3 — evict oldest if full)
      let loadedSlugs = s.loadedSlugs.includes(slug)
        ? s.loadedSlugs
        : [...s.loadedSlugs, slug];
      if (loadedSlugs.length > MAX_LOADED_DRINKS) {
        loadedSlugs = loadedSlugs.slice(loadedSlugs.length - MAX_LOADED_DRINKS);
      }
      // Reset count + checks for this drink (whether new or reload)
      return gc({
        ...s,
        loadedSlugs,
        activeSlug: slug,
        cocktailCounts: { ...s.cocktailCounts, [slug]: DEFAULT_COCKTAIL_COUNT },
        checkedIngredients: { ...s.checkedIngredients, [slug]: {} },
        checkedFinishSteps: { ...s.checkedFinishSteps, [slug]: {} },
      });
    });
  }, []);

  const selectFromTray = useCallback((slug: string) => {
    setState((s) => {
      if (!s.loadedSlugs.includes(slug)) return s;
      return { ...s, activeSlug: slug };
    });
  }, []);

  const removeLoaded = useCallback((slug: string) => {
    setState((s) => {
      const loadedSlugs = s.loadedSlugs.filter((x) => x !== slug);
      const activeSlug = s.activeSlug === slug ? loadedSlugs[0] ?? null : s.activeSlug;
      return gc({ ...s, loadedSlugs, activeSlug });
    });
  }, []);

  const setCocktailCount = useCallback((slug: string, n: number) => {
    setState((s) => ({
      ...s,
      cocktailCounts: { ...s.cocktailCounts, [slug]: clamp(Math.floor(n), 1, 99) },
    }));
  }, []);

  const toggleIngredient = useCallback((slug: string, ingredientId: string) => {
    setState((s) => {
      const current = s.checkedIngredients[slug] || {};
      const next = { ...current, [ingredientId]: !current[ingredientId] };
      return {
        ...s,
        checkedIngredients: { ...s.checkedIngredients, [slug]: next },
      };
    });
  }, []);

  const toggleFinishStep = useCallback((slug: string, finishStepId: string) => {
    setState((s) => {
      const current = s.checkedFinishSteps[slug] || {};
      const next = { ...current, [finishStepId]: !current[finishStepId] };
      return {
        ...s,
        checkedFinishSteps: { ...s.checkedFinishSteps, [slug]: next },
      };
    });
  }, []);

  const resetCocktail = useCallback((slug: string) => {
    setState((s) => ({
      ...s,
      cocktailCounts: { ...s.cocktailCounts, [slug]: DEFAULT_COCKTAIL_COUNT },
      checkedIngredients: { ...s.checkedIngredients, [slug]: {} },
      checkedFinishSteps: { ...s.checkedFinishSteps, [slug]: {} },
    }));
  }, []);

  const clearChecks = useCallback((slug: string) => {
    setState((s) => ({
      ...s,
      checkedIngredients: { ...s.checkedIngredients, [slug]: {} },
      checkedFinishSteps: { ...s.checkedFinishSteps, [slug]: {} },
    }));
  }, []);

  const endRound = useCallback(() => {
    setState({
      loadedSlugs: [],
      activeSlug: null,
      cocktailCounts: {},
      checkedIngredients: {},
      checkedFinishSteps: {},
      filter: "today",
    });
  }, []);

  const setFilter = useCallback((filter: FilterKind) => {
    setState((s) => ({ ...s, filter }));
  }, []);

  const actions = useMemo(
    () => ({
      selectFromList,
      selectFromTray,
      removeLoaded,
      setCocktailCount,
      toggleIngredient,
      toggleFinishStep,
      resetCocktail,
      clearChecks,
      endRound,
      setFilter,
    }),
    [
      selectFromList,
      selectFromTray,
      removeLoaded,
      setCocktailCount,
      toggleIngredient,
      toggleFinishStep,
      resetCocktail,
      clearChecks,
      endRound,
      setFilter,
    ]
  );

  return { state, ...actions };
}
